import React, { useMemo, useRef, useState } from "react";
import { ProgressSpinner } from "primereact/progressspinner";
import useDatasetIdentifiersStore from "store/dataset-identifiers-store";
import useChartDialogStore from "store/chart-dialog-store";
import { T } from "services/lang/LanguageService";
import { useGetRecentData } from "api/api-hooks/datasets-hooks";
import { GaugeComponent, SubArc, Tick } from "react-gauge-component";
import {
  DEFAULT_DARK_COLOR,
  DEFAULT_RED_COLOR,
  DialIndicatorProps,
  DOUBLE_CLICK_THRESHOLD,
  LastZone,
  pointerConfig,
  ValueType,
  Zone,
} from "./DialIndicatorTypes";

interface DatasetZoneWithOverriddenValue {
  value: string | number;
  identifier: string;
  overriddenValue?: string;
}

export default function DialIndicator(props: DialIndicatorProps) {
  const [currDatasetZonesWithOverrValues, setCurrDatasetZonesWithOverrValues] = useState<
    DatasetZoneWithOverriddenValue[]
  >([]);
  const clickTimeRef = useRef(Date.now());
  const selectedIdsStore = useDatasetIdentifiersStore();
  const { openDialog } = useChartDialogStore();

  const zonesIds = useMemo(() => {
    return props.zones.reduce<string[]>((acc, z) => {
      if (typeof z.maxValue === "string") {
        acc.push(z.maxValue);
      }
      return acc;
    }, []);
  }, [props.zones]);

  const identifiers = useMemo(() => {
    const ids = [props.valueDataset];
    if (typeof props.minValue === "string") ids.push(props.minValue);
    if (typeof props.maxValue === "string") ids.push(props.maxValue);
    ids.push(...zonesIds.map(String));
    return ids;
  }, [props.valueDataset, props.minValue, props.maxValue]);

  const { data, isLoading } = useGetRecentData(identifiers, {
    options: {
      refetchInterval: (props.updateFrequency ?? 5) * 1000,
    },
  });

  const minValue = useMemo(() => {
    if (props.minValueType === ValueType.DATASET) {
      return data?.find(d => d.identifier === props.minValue)?.data[0]?.value ?? 0;
    }
    return (props.minValue as number) ?? 0;
  }, [props.minValue, props.minValueType, data]);

  const maxValue = useMemo(() => {
    if (props.maxValueType === ValueType.DATASET) {
      return data?.find(d => d.identifier === props.maxValue)?.data[0]?.value ?? 0;
    }
    return (props.maxValue as number) ?? 0;
  }, [props.maxValue, props.maxValueType, data]);

  const onButtonClick = () => {
    if (!props.valueDataset) return;

    const now = Date.now();
    const isDoubleClick = now - clickTimeRef.current < DOUBLE_CLICK_THRESHOLD;

    if (isDoubleClick) {
      selectedIdsStore.addIdentifier(props.valueDataset);
      openDialog();
    }

    clickTimeRef.current = now;
  };

  const subArcs = useMemo(() => {
    const min = minValue;
    const max = maxValue;

    const zones: SubArc[] =
      props.zones
        ?.map(zone => {
          if (typeof zone.maxValue === "string" && zone.valueType === ValueType.DATASET) {
            const zonesWithOverriddenValues = props.zones.map(z => {
              const value = data?.find(d => d.identifier === z.maxValue)?.data[0]?.value;
              if (z.overriddenValue && z.valueType === ValueType.DATASET) {
                return {
                  value: value?.toFixed(2),
                  identifier: z.maxValue as string,
                  overriddenValue: z.overriddenValue,
                };
              }
              return undefined;
            });
            setCurrDatasetZonesWithOverrValues(
              zonesWithOverriddenValues.filter(Boolean) as DatasetZoneWithOverriddenValue[]
            );
            return {
              limit: data?.find(d => d.identifier === zone.maxValue)?.data[0]?.value ?? 0,
              color: zone.color,
              showTick: true,
            };
          }
          return {
            limit: Number(zone.maxValue),
            color: zone.color,
            showTick: true,
          };
        })
        .filter(subArc => subArc.limit >= min && subArc.limit <= max) ?? [];

    zones.sort((a, b) => (a.limit ?? 0) - (b.limit ?? 0));
    zones.push({ color: props.lastZone?.color ?? DEFAULT_RED_COLOR });
    return zones;
  }, [props.zones, data, minValue, maxValue, props.lastZone]);

  const currentValue = data?.[0]?.data?.[0]?.value ?? 0;

  const ticks: Tick[] = props.ticks
    ?.filter(tick => tick.value >= minValue && tick.value <= maxValue)
    .map(tick => ({
      value: tick.value,
      label: tick.overriddenValue !== undefined ? String(tick.overriddenValue) : undefined,
    }));

  const unit = data?.[0]?.unit ?? "";
  const currentZone: Zone | undefined = props.zones?.find(z => currentValue <= Number(z.maxValue));
  const handleMatchColorWithArc = (zone: Zone | LastZone): boolean => Boolean(zone.highlightValue);

  const marginInPercent = useMemo(() => {
    if (props.hideTicks) {
      return { top: 0, bottom: 0, left: 0.12, right: 0.12 };
    }

    const format = (v: number) => `${v}${unit}`;

    const minLabelLength = format(Number(minValue.toFixed(2))).length;
    const maxLabelLength = format(Number(maxValue.toFixed(2))).length;

    const left = maxLabelLength * 0.0255;
    const right = minLabelLength * 0.0255;

    return { top: 0.08, bottom: 0, left, right };
  }, [minValue, maxValue, props.hideTicks, data, unit]);

  const marginLeft = parseFloat(marginInPercent.left.toString());
  const marginRight = parseFloat(marginInPercent.right.toString());
  const horizontalOffset = ((marginRight - marginLeft) / 2) * 100;

  const handleValueColor = () => {
    if (currentZone && handleMatchColorWithArc(currentZone)) {
      return currentZone.color;
    }
    if (!currentZone && handleMatchColorWithArc(props.lastZone)) {
      return props.lastZone.color;
    }
    return DEFAULT_DARK_COLOR;
  };

  const valueLabelPosition: React.CSSProperties = {
    position: "absolute",
    bottom: 0,
    left: `calc(50% + ${horizontalOffset}%)`,
    transform: "translateX(-50%)",
    height: "3rem",
    fontSize: "2.5rem",
    fontWeight: "700",
    color: handleValueColor(),
  };

  if (identifiers.length !== new Set(identifiers).size) {
    return (
      <div className="bg-red-100 p-2 border-round-md">
        {T("error.message.there-are-duplicated-datasets-in-the-config")}
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex justify-content-center align-items-center">
        <ProgressSpinner />
      </div>
    );
  }

  if (!props.valueDataset) {
    return (
      <div className="bg-red-100 p-2 border-round-md">{T("error.value-dataset-is-required")}</div>
    );
  }

  if (!data) {
    return <div className="bg-red-100 p-2 border-round-md">{T("error.no-available-data")}</div>;
  }

  if (minValue >= maxValue) {
    return (
      <div className="bg-red-100 p-2 border-round-md">
        {T("error.min-value-must-be-less-than-max-value")}
      </div>
    );
  }

  const getValue = (val: string): string => {
    const numericVal = Number(val);
    const formattedVal = Number(numericVal.toFixed(2));
    const areEqual = (a: number, b: number) => Math.abs(a - b) < 0.0001;
    const isValidOverride = (override?: string | number) =>
      override !== undefined && override.toString().trim().length > 0;

    if (
      areEqual(formattedVal, Number(minValue.toFixed(2))) &&
      isValidOverride(props.minValueOverride)
    ) {
      return props.minValueOverride!;
    }

    if (
      areEqual(formattedVal, Number(maxValue.toFixed(2))) &&
      isValidOverride(props.maxValueOverride)
    ) {
      return props.maxValueOverride!;
    }

    const tickMatch = props.ticks?.find(t => areEqual(t.value, formattedVal));
    if (isValidOverride(tickMatch?.overriddenValue)) {
      return tickMatch!.overriddenValue!.toString();
    }

    const datasetZoneMatch = currDatasetZonesWithOverrValues.find(z =>
      areEqual(Number(z.value), formattedVal)
    );

    if (isValidOverride(datasetZoneMatch?.overriddenValue)) {
      return datasetZoneMatch!.overriddenValue!;
    }

    const staticZoneMatch = props.zones?.find(
      z => typeof z.maxValue === "number" && areEqual(z.maxValue, formattedVal)
    );
    if (isValidOverride(staticZoneMatch?.overriddenValue)) {
      return staticZoneMatch!.overriddenValue!.toString();
    }

    return `${formattedVal}${unit}`;
  };

  return (
    <div
      style={{
        position: "relative",
        width: "100%",
        height: "100%",
        paddingBottom: "1.5rem",
        cursor: "pointer",
      }}
      onClick={onButtonClick}
    >
      <div style={{ width: "100%", height: "100%", position: "relative" }}>
        <GaugeComponent
          className="gauge-component-style"
          type="semicircle"
          arc={{
            width: 0.2,
            padding: 0.01,
            cornerRadius: 5,
            subArcs,
          }}
          pointer={pointerConfig}
          marginInPercent={marginInPercent}
          labels={{
            valueLabel: { hide: true },
            tickLabels: {
              type: "outer",
              defaultTickValueConfig: {
                hide: props.hideTicks,
                formatTextValue: (val: string) => getValue(val),
                style: {
                  fontSize: props.ticksFontSize || 9,
                  fill: DEFAULT_DARK_COLOR,
                },
              },
              defaultTickLineConfig: {
                hide: props.hideTicks,
                color: DEFAULT_DARK_COLOR,
              },
              ticks,
            },
          }}
          value={currentValue}
          minValue={minValue}
          maxValue={maxValue}
        />
      </div>
      <div
        style={valueLabelPosition}
      >{`${currentValue.toFixed(data?.[0]?.decimalPlaces ?? 2)} ${unit}`}</div>
    </div>
  );
}
